<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";

import { SOCIAL_ICONS } from "./social-icons";
import { useSettings } from "../composables/useSettings";

/**
 * The foot of every page: the band's accounts, the legal page, the copyright.
 *
 * Lifted out of App.vue when the icons arrived. The shell there is markup and
 * a burger menu; this has a data source of its own, and a component is what
 * lets it be mounted on its own in a test rather than behind a whole
 * application.
 *
 * There is no loading state and no error state. The icons appear when the
 * answer is in, and a footer without them - because the request failed, or
 * because no account is filled in, or because this is the prerendered file
 * where nothing is fetched - is the footer this site had until now. See
 * useSettings for why that is the whole error handling.
 */
const { socialLinks } = useSettings();

/**
 * What can actually be drawn, in the order the CMS sent it.
 *
 * flatMap rather than filter + map, so the icon is looked up once and the
 * result carries it: a platform without a logo in this version is dropped
 * here, and everything downstream can assume there is something to render.
 */
const icons = computed(() =>
  socialLinks.value.flatMap((link) => {
    const icon = SOCIAL_ICONS[link.platform];
    return icon ? [{ platform: link.platform, url: link.url, ...icon }] : [];
  }),
);
</script>

<template>
  <footer>
    <!--
      A list, because that is what a row of links is, and a screen reader then
      announces how many accounts there are. v-if rather than an empty <ul>: a
      list with no items still occupies its margins, and the footer of a band
      with no accounts has to look exactly as it did before.
    -->
    <ul v-if="icons.length > 0" class="social-links">
      <li v-for="icon in icons" :key="icon.platform">
        <!--
          target="_blank" because the account is somewhere else and a visitor
          reading the page should keep it. rel="noopener noreferrer" with it:
          noopener denies the opened page a handle on this window, which it
          would otherwise have and could navigate.

          The name is on the link, not next to the icon: the logo is the label
          for anyone who can see it, and aria-label is what puts the same word
          - "Instagram", not "Link" - into a screen reader. The <svg> is hidden
          from the accessibility tree so it cannot be announced twice, and
          focusable="false" keeps old Edge from making it a tab stop of its own.
        -->
        <a
          class="social-link"
          :href="icon.url"
          :aria-label="icon.label"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg
            class="social-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path :d="icon.path" />
          </svg>
        </a>
      </li>
    </ul>

    <RouterLink class="footer-legal" to="/impressum">
      Impressum und Datenschutz
    </RouterLink>
    <p class="footer-copyright">&copy; tubetänzig</p>
  </footer>
</template>
